import { FC, useCallback, useState } from 'react'
import { Button, FormDialog, Notification } from '@mondra/ui-components'
import { useFormik } from 'formik'
import InputItem from '../../../../components/InputItem'
import { IComponentMaterialRow, IComponentTaxonomyConfiguration } from '../types'
import {
  EI_NAME_MAX_LENGTH,
  EXACT_MATCH_OPTIONS,
  formatProcessingActivityLabel,
  fromExactMatchValue,
  materialModalSchema,
  toExactMatchValue,
  toNumberOrNull,
  validateMaterialRow,
} from '../utils'

interface MaterialFormProps {
  addMaterial: (row: IComponentMaterialRow) => void
  configuration: IComponentTaxonomyConfiguration
  existingMaterials: IComponentMaterialRow[]
}

const MaterialForm: FC<MaterialFormProps> = ({ addMaterial, configuration, existingMaterials }) => {
  const [openForm, setOpenForm] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)
  const [materialOptions, setMaterialOptions] = useState<
    IComponentTaxonomyConfiguration['packagingMaterialFunctionalUnits']
  >([])

  const formik = useFormik({
    initialValues: {
      categoryId: 0,
      defaultProcessingActivityId: null as number | null,
      defaultRecycledContent: '' as string | number,
      eiName: '',
      functionalUnitId: null as number | null,
      isExactMatch: null as boolean | null,
    },
    validationSchema: materialModalSchema,
    onSubmit: values => {
      const catId = +values.categoryId
      const fuId = values.functionalUnitId ? +values.functionalUnitId : null

      const error = validateMaterialRow(catId, fuId, existingMaterials)
      if (error) {
        setModalError(error)
        return
      }

      const selectedCategory = configuration.packagingMaterialCategories.find(c => c.id === catId)
      const selectedMaterial = fuId
        ? configuration.packagingMaterialFunctionalUnits.find(fu => fu.id === fuId)
        : null

      // The four combination fields are set here as well as on the expanded row, so a material can
      // be added complete in one pass. They stay editable on the row afterwards.
      const selectedProcessingActivity = values.defaultProcessingActivityId
        ? configuration.packagingProcessingActivities.find(
            pa => pa.id === Number(values.defaultProcessingActivityId)
          )
        : null

      const row: IComponentMaterialRow = {
        defaultProcessingActivityId: toNumberOrNull(values.defaultProcessingActivityId),
        defaultProcessingActivityName: selectedProcessingActivity?.name ?? null,
        defaultRecycledContent: toNumberOrNull(values.defaultRecycledContent),
        eiName: values.eiName === '' ? null : values.eiName,
        id: -Date.now(),
        isExactMatch: values.isExactMatch ?? null,
        packagingMaterialCategoryCode: selectedCategory?.code ?? null,
        packagingMaterialCategoryId: catId,
        packagingMaterialCategoryName: selectedCategory?.name ?? '',
        packagingMaterialFunctionalUnitCode: selectedMaterial?.code ?? null,
        packagingMaterialFunctionalUnitId: fuId,
        packagingMaterialFunctionalUnitName: selectedMaterial?.name ?? null,
      }

      addMaterial(row)
      setTimeout(() => setOpenForm(false), 500)
    },
  })

  const onOpen = useCallback(() => {
    setOpenForm(true)
    setModalError(null)
    setMaterialOptions([])
    formik.setValues({
      categoryId: 0,
      defaultProcessingActivityId: null,
      defaultRecycledContent: '',
      eiName: '',
      functionalUnitId: null,
      isExactMatch: null,
    })
    formik.setTouched({}, false)
  }, [])

  const handleCategoryChange = useCallback(
    e => {
      const catId = Number(e.target.value)
      formik.setFieldValue('categoryId', catId)
      formik.setFieldValue('functionalUnitId', null)
      setModalError(null)
      setMaterialOptions(
        catId > 0
          ? configuration.packagingMaterialFunctionalUnits.filter(fu => fu.categoryId === catId)
          : []
      )
    },
    [configuration.packagingMaterialFunctionalUnits]
  )

  const handleMaterialChange = useCallback(e => {
    const val = e.target.value
    formik.setFieldValue('functionalUnitId', val === '' ? null : Number(val))
    setModalError(null)
  }, [])

  // Both selects are coerced on the way in, so the dialog hands the row the same shapes the edit
  // page stores: a number or null for the activity id, a tri-state boolean for exact match.
  const handleProcessingActivityChange = useCallback(e => {
    formik.setFieldValue('defaultProcessingActivityId', toNumberOrNull(e.target.value))
  }, [])

  const handleExactMatchChange = useCallback(e => {
    formik.setFieldValue('isExactMatch', toExactMatchValue(e.target.value))
  }, [])

  return (
    <>
      <Button className="mt-5" iconType="add" onClick={onOpen} variant="primary">
        Add material
      </Button>
      <FormDialog
        formId="material-form"
        onClose={() => setTimeout(() => setOpenForm(false), 500)}
        onPrimaryButtonClick={formik.handleSubmit}
        open={openForm}
        primaryBtnText="Add"
        title="Add material"
      >
        <form id="material-form" onSubmit={formik.handleSubmit}>
          <div className="component-taxonomy-modal-inputs">
            {modalError && (
              <div className="component-taxonomy-full mb-3">
                <Notification description={modalError} label="Validation:" type="error" />
              </div>
            )}
            <InputItem
              defaultValue={formik.values.categoryId}
              description="Select a material category"
              formik={formik}
              formikName="categoryId"
              name="Category"
              onChange={handleCategoryChange}
              options={[
                { label: '-- Select category --', value: 0 },
                ...configuration.packagingMaterialCategories.map(c => ({
                  label: c.name,
                  value: c.id,
                })),
              ]}
              showAsterisk
              type="select"
            />
            <InputItem
              defaultValue={formik.values.functionalUnitId ?? ''}
              description="Select a specific material, or leave empty to include all materials in the category"
              disabled={materialOptions.length === 0}
              formik={formik}
              formikName="functionalUnitId"
              name="Material name"
              onChange={handleMaterialChange}
              options={materialOptions.map(m => ({ label: m.name, value: m.id }))}
              type="select"
            />
            <InputItem
              defaultValue={formik.values.defaultProcessingActivityId ?? -1}
              description="The published packaging processing activity this material defaults to."
              formik={formik}
              formikName="defaultProcessingActivityId"
              name="Processing Name"
              onChange={handleProcessingActivityChange}
              options={configuration.packagingProcessingActivities.map(pa => ({
                label: formatProcessingActivityLabel(pa),
                value: pa.id,
              }))}
              type="select"
            />
            <InputItem
              addonTitle="%"
              defaultValue={formik.values.defaultRecycledContent}
              description="Default recycled content as a percentage, 0 to 100. Leave empty if not stated."
              formik={formik}
              formikName="defaultRecycledContent"
              name="Default Recycled Content"
              onChange={formik.handleChange}
              type="input"
            />
            <InputItem
              defaultValue={formik.values.eiName}
              description={`The Ecoinvent activity name for this material. Up to ${EI_NAME_MAX_LENGTH} characters.`}
              formik={formik}
              formikName="eiName"
              name="EI Name"
              onChange={formik.handleChange}
              type="input"
            />
            <InputItem
              defaultValue={fromExactMatchValue(formik.values.isExactMatch)}
              description="Whether the Ecoinvent activity is an exact match for this material or a proxy."
              formik={formik}
              formikName="isExactMatch"
              name="Exact Match or Proxy"
              onChange={handleExactMatchChange}
              options={EXACT_MATCH_OPTIONS}
              type="select"
            />
          </div>
        </form>
      </FormDialog>
    </>
  )
}

export default MaterialForm
